import React from "react";

export interface BoolContextCardProps {
  /** Prepopulated context shown below the question headline (e.g. appointment
   *  details), keyed by label. Each entry renders as the value (left) with its
   *  label (right), matching the Figma appointment card. Insertion order is
   *  preserved. */
  context: Record<string, string>;
}

/**
 * Read-only context card rendered below a bool question when prepopulated
 * context data is present (Figma "Are you in the office? 1.1" appointment card).
 * Each row shows the value on the left and its label on the right, per the
 * Figma design — the flow engine maps real workflow/prepopulation data into
 * this shape.
 */
const BoolContextCard: React.FC<BoolContextCardProps> = ({ context }) => {
  const entries = Object.entries(context);
  if (entries.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="bool-context-card"
      className="rounded-lg bg-[#E8EDF2] shadow divide-y divide-gray-200"
    >
      {entries.map(([label, value]) => (
        <div
          key={label}
          className="flex items-center justify-between gap-3 px-4 py-3 min-h-[44px]"
        >
          <span className="min-w-0 break-words text-black">{value}</span>
          {/* gray-600, not gray-500: the label sits on the #E8EDF2 card, which
              costs ~0.7 against white. gray-500 measures 4.83:1 on white but
              only 4.10:1 here, under the 4.5:1 this 16px text needs. */}
          <span className="shrink-0 text-gray-600 text-right">{label}</span>
        </div>
      ))}
    </div>
  );
};

export default BoolContextCard;
